import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Project } from '../objects/project';
import { CargoSpace, Load, Notification } from '../types';
import { ProjectService } from './project.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  public notifications: Observable<Notification[]>;
  public notifiactionsSubject: BehaviorSubject<Notification[]>;
  public currentNotification: Notification;
  public currentNotifications: Notification[];
  private currentWorkSpace: number;
  private currentStopIndex: number;

  constructor(private translate: TranslateService, private projectService: ProjectService) {
    this.notifiactionsSubject = new BehaviorSubject(new Array<Notification>());
    this.notifications = this.notifiactionsSubject.asObservable();
    this.notifications.subscribe((notifications) => {
      this.currentNotifications = notifications;
      this.setCurrentNotification();
    });

    this.projectService.currentWorkSpaceIndex.subscribe(
      (currentWorkSpace: number) => (this.currentWorkSpace = currentWorkSpace),
    );
    this.projectService.currentStopIndex.subscribe(
      (currentStopIndex: number) => (this.currentStopIndex = currentStopIndex),
    );
  }

  public deleteNotification(): void {
    this.currentNotification = undefined;
    this.currentNotifications.shift();
    this.notifiactionsSubject.next(this.currentNotifications);
  }

  public isNotifiaction(notificationText: string) {
    return !!this.currentNotifications.find(
      (notification) => notification?.text === notificationText,
    );
  }

  public removeNotification(notificationText: string) {
    const notificationIndex = this.currentNotifications.findIndex(
      (notification) => notification?.text === notificationText,
    );
    if (notificationIndex >= 0) {
      if (this.currentNotification?.text === this.currentNotifications[notificationIndex].text) {
        this.currentNotification = undefined;
      }
      this.currentNotifications.splice(notificationIndex, 1);
      this.notifiactionsSubject.next(this.currentNotifications);
    }
  }

  public addNotification(
    type: 'good' | 'danger' | 'warning',
    text: string,
    timeToClose?: number,
  ): void {
    if (!this.currentNotifications.find((notification) => notification?.text === text)) {
      const newNotifications = [...this.currentNotifications, { type, text, timeToClose }];
      this.notifiactionsSubject.next(newNotifications);
    }
  }

  public deleteAllNotification(): void {
    this.notifiactionsSubject.next([]);
  }

  public generateCOGNotification(result: Project, toFirstEmpty?: boolean) {
    let cargoSpaceWithCOGflagFalse: CargoSpace;
    if (toFirstEmpty) {
      result.workSpaces.forEach(
        (workSpace) =>
          (cargoSpaceWithCOGflagFalse = workSpace.cargoSpaces.find(
            (cargoSpace) => cargoSpace.extraFeatures.COGflag === false,
          )),
      );
    } else {
      const workSpace =
        this.currentStopIndex >= 0
          ? result.workSpaces[this.currentWorkSpace].stops[this.currentStopIndex]
          : result.workSpaces[this.currentWorkSpace];

      cargoSpaceWithCOGflagFalse = workSpace.cargoSpaces.find(
        (cargoSpace) => cargoSpace.extraFeatures.COGflag === false,
      );
    }
    if (cargoSpaceWithCOGflagFalse) {
      this.addNotification(
        'warning',
        this.translate.instant('notification_center_of_gravity_exceeded'),
      );
    }
  }

  public generateLoadNotDisplayedNotification(addedLoad: Partial<Load>, project: Project): void {
    const cargoSpace: CargoSpace = project.workSpaces[this.currentWorkSpace].cargoSpaces[0];
    const cargoSpaceSet: CargoSpace = project.workSpaces[this.currentWorkSpace].cargoSpaces[1];
    const cargoSpaceLength: number = cargoSpaceSet
      ? cargoSpaceSet.dimensions.length + cargoSpace.dimensions.length + cargoSpace.dimensions.gap
      : cargoSpace.dimensions.length;
    const cargoSpaceWidth = cargoSpaceSet
      ? cargoSpaceSet.dimensions.width > cargoSpace.dimensions.width
        ? cargoSpaceSet.dimensions.width
        : cargoSpace.dimensions.width
      : cargoSpace.dimensions.width;
    const scalar = project.options.unit === 'cm' || project.options.unit === 'mm' ? 1 : 2.54;
    const gapFromOutsideSpace = 450 * scalar;
    if (
      addedLoad.length > 2 * cargoSpaceLength ||
      addedLoad.width > 2 * cargoSpaceWidth + gapFromOutsideSpace
    ) {
      this.addNotification('warning', this.translate.instant('notification_loads_not_displayed'));
    }
  }

  private setCurrentNotification() {
    if (this.currentNotifications.length > 0 && !this.currentNotification) {
      this.currentNotification = this.currentNotifications[0];
    }
  }
}
